import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { Clock, Plus, Trash2 } from 'lucide-react';
import { useShiftConfig, useUpdateShiftConfig } from '@/hooks/useShiftConfig';
import { DEFAULT_SHIFT_FRAMES, isOvernightShift, normalizeHHMM, validateShiftFrames, type ShiftFrame } from '@/utils/shiftConfig';

const makeShift = (index: number): ShiftFrame => ({
  id: `custom-${Date.now()}-${index}`,
  label: 'Custom Shift',
  start: '08:00',
  end: '16:00',
  isOvernight: false,
});

export const ShiftTimeFramesSettings = () => {
  const { data: savedShifts = DEFAULT_SHIFT_FRAMES, isLoading } = useShiftConfig();
  const updateMut = useUpdateShiftConfig();
  const [shifts, setShifts] = useState<ShiftFrame[]>(DEFAULT_SHIFT_FRAMES);

  useEffect(() => {
    setShifts(savedShifts);
  }, [savedShifts]);

  const updateShift = (id: string, patch: Partial<ShiftFrame>) => {
    setShifts((prev) => prev.map((shift) => {
      if (shift.id !== id) return shift;
      const next = { ...shift, ...patch };
      return { ...next, isOvernight: isOvernightShift(next.start, next.end) };
    }));
  };

  const save = () => {
    const normalized = shifts.map((shift) => ({
      ...shift,
      label: shift.label.trim(),
      start: normalizeHHMM(shift.start) || shift.start,
      end: normalizeHHMM(shift.end) || shift.end,
      isOvernight: isOvernightShift(shift.start, shift.end),
    }));
    const error = validateShiftFrames(normalized);
    if (error) {
      toast({ variant: 'destructive', title: 'Unable to save shifts', description: error });
      return;
    }
    updateMut.mutate(normalized, {
      onSuccess: () => toast({ title: 'Shift Time Frames Saved', description: 'Reports will use these shift windows.' }),
      onError: (e: any) => toast({ variant: 'destructive', title: 'Error', description: e.message || 'Failed to save shifts.' }),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" /> Shift Time Frames
        </CardTitle>
        <CardDescription>Agency-wide shift windows used by Shift Reports.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          {shifts.map((shift, index) => (
            <div key={shift.id} className="grid grid-cols-1 md:grid-cols-[1fr_120px_120px_auto_auto] gap-3 items-end rounded-md border p-3">
              <div className="space-y-1.5">
                <Label>Shift Name</Label>
                <Input value={shift.label} onChange={(e) => updateShift(shift.id, { label: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Start</Label>
                <Input type="time" value={shift.start} onChange={(e) => updateShift(shift.id, { start: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>End</Label>
                <Input type="time" value={shift.end} onChange={(e) => updateShift(shift.id, { end: e.target.value })} />
              </div>
              <Badge variant="secondary" className="justify-center h-9">
                {shift.isOvernight ? 'Overnight' : 'Same Day'}
              </Badge>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setShifts((prev) => prev.filter((item) => item.id !== shift.id))}
                disabled={shifts.length === 1}
                aria-label={`Remove ${shift.label || `shift ${index + 1}`}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:justify-between">
          <Button type="button" variant="outline" onClick={() => setShifts((prev) => [...prev, makeShift(prev.length)])}>
            <Plus className="h-4 w-4 mr-2" /> Add Shift
          </Button>
          <Button onClick={save} disabled={isLoading || updateMut.isPending}>
            {updateMut.isPending ? 'Saving…' : 'Save Shift Frames'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};